import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  SkeletonBodyText,
  EmptyState,
  Box,
  Text,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { Rental } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function ClinicTomorrowOverdue() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clinic-tomorrow-overdue'],
    queryFn: () => clinicPortalApi.getTomorrowOverdueOrders(),
  });

  const rows = (data || []).map((rental: Rental) => [
    orderNumber(rental),
    rental.patientName || 'N/A',
    rental.items?.[0]?.productName || 'N/A',
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    <Badge key={rental.id} tone="warning">Ending Tomorrow</Badge>,
    rental.patientPhone || 'N/A',
  ]);

  if (error) {
    return (
      <Page title="Tomorrow Overdue">
        <Card>
          <EmptyState
            heading="Error loading report"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Tomorrow Overdue">
      <Card>
        {isLoading ? (
          <SkeletonBodyText lines={8} />
        ) : rows.length === 0 ? (
          <Box padding="400">
            <EmptyState
              heading="No rentals ending tomorrow"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>There are no rentals ending tomorrow.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Order #', 'Patient Name', 'Product', 'Start Date', 'End Date', 'Status', 'Phone']}
              rows={rows}
            />
            <Box paddingBlockStart="400">
              <Text as="p" tone="subdued" alignment="center">
                {rows.length} rental{rows.length !== 1 ? 's' : ''} ending tomorrow
              </Text>
            </Box>
          </>
        )}
      </Card>
    </Page>
  );
}
